import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { routes } from '@lib/routes';

const contactSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  emailConfirmation: z
    .string()
    .min(1, 'Please confirm your email'),
  phone: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^\+?[\d\s-()]+$/.test(val),
      'Please enter a valid phone number'
    ),
  consent: z
    .boolean()
    .refine((val) => val === true, 'You must agree to receive results via email'),
}).refine((data) => data.email === data.emailConfirmation, {
  message: 'Emails do not match',
  path: ['emailConfirmation'],
});

type ContactFormData = z.infer<typeof contactSchema>;

export default function Contact() {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    mode: 'onChange',
  });

  const onSubmit = (data: ContactFormData) => {
    // Store contact info in localStorage
    localStorage.setItem('checkio_contact', JSON.stringify({
      email: data.email,
      phone: data.phone || null,
    }));
    
    navigate(routes.payment);
  };

  return (
    <div className="min-h-screen bg-veritas-gray-50 font-sans text-veritas-navy">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="veritas-heading-section mb-4 text-center">Contact Information</h1>
        <p className="veritas-body text-center mb-12">
          We'll only use your email to deliver your verification report
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-white/90 backdrop-blur-md rounded-2xl shadow-veritas-soft border border-white/60 p-8">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-sans font-semibold text-veritas-navy mb-2">
              Email Address <span className="text-red-600">*</span>
            </label>
            <input
              id="email"
              type="email"
              {...register('email')}
              className={`w-full bg-veritas-gray-50 border rounded-lg px-4 py-3 text-veritas-navy placeholder-veritas-gray-400 focus:border-veritas-navy focus:outline-none focus:ring-2 focus:ring-veritas-navy/20 transition-all ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-veritas-navy/20'}`}
              placeholder="your.email@example.com"
            />
            {errors.email && (
              <p className="mt-2 text-sm text-red-600 font-sans">{errors.email.message}</p>
            )}
          </div>

          {/* Email Confirmation Field */}
          <div>
            <label htmlFor="emailConfirmation" className="block text-sm font-sans font-semibold text-veritas-navy mb-2">
              Confirm Email Address <span className="text-red-600">*</span>
            </label>
            <input
              id="emailConfirmation"
              type="email"
              {...register('emailConfirmation')}
              className={`w-full bg-veritas-gray-50 border rounded-lg px-4 py-3 text-veritas-navy placeholder-veritas-gray-400 focus:border-veritas-navy focus:outline-none focus:ring-2 focus:ring-veritas-navy/20 transition-all ${errors.emailConfirmation ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-veritas-navy/20'}`}
              placeholder="your.email@example.com"
            />
            {errors.emailConfirmation && (
              <p className="mt-2 text-sm text-red-600 font-sans">
                {errors.emailConfirmation.message}
              </p>
            )}
          </div>

          {/* Phone Field (Optional) */}
          <div>
            <label htmlFor="phone" className="block text-sm font-sans font-semibold text-veritas-navy mb-2">
              Phone Number <span className="veritas-body-light text-xs">(Optional)</span>
            </label>
            <input
              id="phone"
              type="tel"
              {...register('phone')}
              className={`w-full bg-veritas-gray-50 border rounded-lg px-4 py-3 text-veritas-navy placeholder-veritas-gray-400 focus:border-veritas-navy focus:outline-none focus:ring-2 focus:ring-veritas-navy/20 transition-all ${errors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-veritas-navy/20'}`}
              placeholder="+1 (555) 123-4567"
            />
            {errors.phone && (
              <p className="mt-2 text-sm text-red-600 font-sans">{errors.phone.message}</p>
            )}
            <p className="mt-2 text-sm veritas-body-light">
              We may contact you if we need additional information
            </p>
          </div>

          {/* Consent Checkbox */}
          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...register('consent')}
                className="mt-1 w-5 h-5 rounded-lg border-veritas-navy/20 bg-veritas-gray-50 text-veritas-navy focus:ring-veritas-navy focus:ring-2"
              />
              <div className="flex-1">
                <span className="text-sm font-sans text-veritas-navy">
                  I agree to receive my verification report via email{' '}
                  <span className="text-red-600">*</span>
                </span>
                <p className="text-xs veritas-body-light mt-1">
                  See our{' '}
                  <a href="#" className="text-veritas-navy underline hover:text-veritas-gray-500 transition-colors">
                    Privacy Policy
                  </a>
                </p>
              </div>
            </label>
            {errors.consent && (
              <p className="mt-2 text-sm text-red-600 font-sans">{errors.consent.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex gap-4 pt-6">
            <button
              type="button"
              onClick={() => navigate(routes.app)}
              className="flex-1 px-6 py-3 border border-veritas-navy/20 text-veritas-navy font-sans font-semibold rounded-lg hover:bg-veritas-gray-200 transition-colors"
            >
              ← Back
            </button>
            <button
              type="submit"
              disabled={!isValid}
              className="flex-1 btn-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue →
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
